/**
 * Quinella cart drawer — behaviour for sections/qs-cart-drawer.liquid.
 *
 * Deliberately a plain script rather than a `@theme/*` module: the drawer is
 * rendered from the layout and has to work on every template, including ones
 * that never pull in Horizon's component graph.
 *
 * Open/closed state lives as a class on <html>, not in the section, because the
 * section's markup is wholesale replaced on every quantity change.
 *
 * All cart mutations go through the Section Rendering API — /cart/change.js is
 * asked for `sections=qs-cart-drawer` so the updated markup comes back in the
 * same round trip that performed the change.
 */
(function () {
  'use strict';

  var SECTION_ID = 'qs-cart-drawer';
  var SECTION_NODE_ID = 'shopify-section-' + SECTION_ID;
  var OPEN_CLASS = 'qs-cd-is-open';
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  var lastFocused = null;
  var previousBodyOverflow = null;
  var inFlight = false;
  var loadedScripts = {};

  function host() {
    return document.getElementById(SECTION_NODE_ID);
  }

  function panel() {
    return document.querySelector('[data-qs-cd-panel]');
  }

  function isOpen() {
    return document.documentElement.classList.contains(OPEN_CLASS);
  }

  /* ------------------------------------------------------------------ open */

  function lockScroll(lock) {
    if (lock) {
      if (previousBodyOverflow === null) previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = previousBodyOverflow || '';
      previousBodyOverflow = null;
    }
  }

  function focusFirst() {
    var p = panel();
    if (!p) return;
    var target = p.querySelector('[data-qs-cd-autofocus]') || p.querySelector(FOCUSABLE);
    if (target) target.focus();
  }

  function open() {
    if (isOpen()) return;
    lastFocused = document.activeElement;
    document.documentElement.classList.add(OPEN_CLASS);
    var p = panel();
    if (p) p.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    document.addEventListener('keydown', onKeydown);
    window.requestAnimationFrame(focusFirst);
  }

  function close() {
    if (!isOpen()) return;
    document.documentElement.classList.remove(OPEN_CLASS);
    var p = panel();
    if (p) p.setAttribute('aria-hidden', 'true');
    lockScroll(false);
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      /* The review dialog opens on top of the drawer and owns Escape while it
         is up — closing the drawer underneath it would strand the customer. */
      if (document.querySelector('.design-review__dialog[open]')) return;
      event.stopPropagation();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    var p = panel();
    if (!p) return;
    var focusable = Array.prototype.filter.call(p.querySelectorAll(FOCUSABLE), function (node) {
      return node.offsetParent !== null;
    });
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------- render */

  function seedLoadedScripts() {
    Array.prototype.forEach.call(document.querySelectorAll('script[src]'), function (node) {
      loadedScripts[node.getAttribute('src')] = true;
    });
  }

  /**
   * A re-rendered section's <script src> tags are inert once injected via
   * innerHTML. The review form only appears once the cart holds a design, so its
   * module can legitimately be missing at first paint — re-append anything new.
   */
  function hydrateScripts(incoming) {
    Array.prototype.forEach.call(incoming.querySelectorAll('script[src]'), function (node) {
      var src = node.getAttribute('src');
      if (!src || loadedScripts[src]) return;
      loadedScripts[src] = true;
      var script = document.createElement('script');
      script.src = src;
      if (node.type) script.type = node.type;
      else script.defer = true;
      document.head.appendChild(script);
    });
  }

  /**
   * @param {string} html - Section markup from the Section Rendering API.
   * @param {string} [restoreSelector] - Element to hand focus back to. The node
   *   the customer clicked is destroyed by the re-render, so without this focus
   *   drops to <body> and escapes the drawer after a single quantity tap.
   */
  function render(html, restoreSelector) {
    var target = host();
    if (!target || !html) return;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var incoming = parsed.getElementById(SECTION_NODE_ID) || parsed.body;

    target.innerHTML = incoming.innerHTML;
    hydrateScripts(incoming);
    syncCount();

    if (!isOpen()) return;

    /* aria-hidden comes back as "true" from Liquid — the drawer's own markup has
       no idea it is currently on screen. */
    var p = panel();
    if (p) p.setAttribute('aria-hidden', 'false');

    var restored = restoreSelector && p ? p.querySelector(restoreSelector) : null;
    if (restored) restored.focus();
    else focusFirst();
  }

  function syncCount() {
    var drawer = document.querySelector('[data-qs-cart-drawer]');
    if (!drawer) return;
    var count = drawer.getAttribute('data-cart-count') || '0';
    Array.prototype.forEach.call(document.querySelectorAll('[data-qs-cart-count]'), function (node) {
      node.textContent = count;
      node.toggleAttribute('hidden', count === '0');
    });
  }

  /**
   * Fallback path: re-request the section on its own. Used when whatever changed
   * the cart didn't hand us rendered markup (a third-party form, say).
   *
   * `no-store` is not optional. Without it the browser will happily replay a
   * cached copy of this URL and repaint the drawer with the cart as it was
   * BEFORE the add — which reads as "the drawer never refreshed".
   */
  function refresh() {
    return fetch(window.location.pathname + '?sections=' + SECTION_ID, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (sections) {
        render(sections[SECTION_ID]);
      })
      .catch(function (error) {
        /* Keep the stale markup rather than blanking the drawer, but say so —
           a silent failure here looks exactly like a drawer that won't update. */
        console.warn('[qs-cart-drawer] could not refresh:', error);
      });
  }

  /* ------------------------------------------------------------- mutations */

  function change(line, quantity, sourceEl) {
    if (inFlight) return;
    inFlight = true;

    var card = sourceEl && sourceEl.closest ? sourceEl.closest('.qs-cd__item') : null;
    if (card) card.setAttribute('data-qs-cd-busy', '');

    /* Same button on the same line after the repaint. When the line is gone
       (quantity hit zero, or the customer removed it) nothing matches and
       render() falls back to the close button. */
    var direction = sourceEl && sourceEl.getAttribute('data-direction');
    var restoreSelector = direction
      ? '[data-qs-cd-qty][data-line="' + line + '"][data-direction="' + direction + '"]'
      : null;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: Number(line),
        quantity: Number(quantity),
        sections: SECTION_ID,
        sections_url: window.location.pathname,
      }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        render(cart.sections && cart.sections[SECTION_ID], restoreSelector);
        /* Let the rest of the theme (cart page, bubbles) know the cart moved. */
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, sourceId: SECTION_ID },
          })
        );
      })
      .catch(function () {
        /* Leave the line as it was; the next click retries. */
      })
      .finally(function () {
        inFlight = false;
        /* Unconditional: on success the card is usually gone with the re-render,
           but if the response carried no section markup the original node is
           still on screen and must not stay permanently disabled. */
        if (card) card.removeAttribute('data-qs-cd-busy');
      });
  }

  /* ---------------------------------------------------------------- wiring */

  /* ------------------------------------------------------- preview download
     "View image" on a design preview. The href points at the Shopify CDN copy
     of the line-item upload, and following it directly is what produces a WebP
     in Chrome: the CDN picks the format from the browser's Accept header, which
     on a navigation advertises WebP and AVIF. That is not something the URL can
     override — image_url's `format` parameter converts only to jpg/pjpg.

     So the file is re-fetched instead. fetch() sends a wildcard Accept header,
     so the CDN has no better format to offer and returns the bytes as stored —
     the PNG the customiser rasterised. Saving it from a blob also means the
     customer gets "design-preview-front.png" rather than the 32-character hash
     Shopify renamed the upload to.

     The extension follows the response's own content type rather than being
     forced to .png, so an unexpected format is labelled honestly instead of
     being a WebP wearing a .png name.

     Any failure — CORS, offline, a browser without createObjectURL — opens the
     original href in a new tab, which is exactly what the link did before. */
  function downloadPreview(url, filename) {
    if (!window.fetch || !window.URL || !URL.createObjectURL) return Promise.reject();
    return fetch(url, { mode: 'cors', credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.blob();
      })
      .then(function (blob) {
        var name = filename || 'design-preview.png';
        if (blob.type && blob.type.indexOf('image/') === 0 && blob.type !== 'image/png') {
          name = name.replace(/\.png$/, '.' + blob.type.slice(6).split('+')[0]);
        }
        var href = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = href;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        /* Revoked on a later turn: Safari has not always finished reading the
           blob by the time click() returns. */
        setTimeout(function () {
          try {
            URL.revokeObjectURL(href);
          } catch (e) {}
        }, 10000);
      });
  }

  document.addEventListener('click', function (event) {
    if (!event.target || typeof event.target.closest !== 'function') return;

    var opener = event.target.closest('[data-qs-cart-open]');
    if (opener) {
      event.preventDefault();
      open();
      return;
    }

    var dl = event.target.closest('[data-qs-cd-download]');
    if (dl && dl.getAttribute('href')) {
      event.preventDefault();
      var href = dl.getAttribute('href');
      downloadPreview(href, dl.getAttribute('data-filename')).catch(function () {
        window.open(href, '_blank', 'noopener');
      });
      return;
    }

    if (!isOpen()) return;

    if (event.target.closest('[data-qs-cd-close]')) {
      event.preventDefault();
      close();
      return;
    }

    var qty = event.target.closest('[data-qs-cd-qty]');
    if (qty) {
      event.preventDefault();
      change(qty.getAttribute('data-line'), qty.getAttribute('data-quantity'), qty);
      return;
    }

    var remove = event.target.closest('[data-qs-cd-remove]');
    if (remove) {
      event.preventDefault();
      change(remove.getAttribute('data-line'), 0, remove);
    }
  });

  /**
   * The customiser (sections/quinella-customizer.liquid) and Horizon's own
   * product forms both announce an add this way. Ignore our own echo so a
   * quantity change doesn't re-open a drawer the customer just closed.
   */
  document.addEventListener('cart:update', function (event) {
    var detail = event.detail || {};
    if (detail.sourceId === SECTION_ID) return;

    /* Prefer markup the add already rendered for us — it was produced by the
       same request that changed the cart, so it is guaranteed current. Only
       fall back to fetching the section when nobody supplied it. */
    var supplied =
      (detail.data && detail.data.sections && detail.data.sections[SECTION_ID]) ||
      (detail.resource && detail.resource.sections && detail.resource.sections[SECTION_ID]);

    if (supplied) {
      render(supplied);
      open();
      return;
    }

    refresh().then(open);
  });

  seedLoadedScripts();
  syncCount();

  window.QSCartDrawer = { open: open, close: close, refresh: refresh };
})();
